// One card per carrier (APC, InPost, Palletways). Copies ChannelCard's SHELL —
// collapsible <details> with title + status pills in the summary. The
// body is a top line (enable + environment) followed by collapsed
// CarrierSections whose summary lines digest their state (merging,
// claims, webhooks, surcharges), then one collapsed panel per account
// (warehouse × credential set) with its own sections.

import { useState } from "react";
import { PiggyBank, Settings2 } from "lucide-react";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import { API_BASE } from "../../../api/base";
import { useReadOnly } from "../../../access/ReadOnly";
import CarrierAccountPanel from "./CarrierAccountPanel";
import CarrierSavingsModal from "./CarrierSavingsModal";
import CarrierClaimsSettingsModal from "./CarrierClaimsSettingsModal";
import SecretField from "./SecretField";
import CarrierSection from "./CarrierSection";
import type { CarrierConfig, CarrierPatch, CarrierSurchargeConfig } from "./types";

interface Props {
    carrier: CarrierConfig;
    hint?: string;
    onUpdated: (next: CarrierConfig) => void;
}

// Capabilities that are carrier-shaped rather than data-driven: add-box
// merging needs a multi-piece consignment (APC only), claims need a
// carrier claims sheet (APC + InPost), webhooks + surcharge config are
// InPost's.
const SUPPORTS_ADD_BOX = new Set(["apc"]);
// Claims: APC on its own template, InPost (2026-10-31) on the generic sheet
// until its template arrives. Keep in step with the BE's
// SUPPORTED_CLAIM_CARRIERS (orderClaims.controller.js).
const SUPPORTS_CLAIMS = new Set(["apc", "inpost"]);
const HAS_WEBHOOKS = new Set(["inpost"]);
const PRODUCTION_API_ORIGIN = "https://wms.jlitrading.co.uk";
const ENVIRONMENT_HINT_KEY: Record<string, string> = {
    inpost: "carriers_environment_hint_inpost",
    palletways: "carriers_environment_hint_palletways",
    dpd: "carriers_environment_hint_dpd",
    cargus: "carriers_environment_hint_cargus",
    dbschenker: "carriers_environment_hint_manual_pallet",
    jas: "carriers_environment_hint_jas",
    raben: "carriers_environment_hint_raben",
};
// Palletways has one live host and no sandbox, so there is nothing for the
// Training / Live switch to choose between. DB Schenker has no API at all
// (manual pallet carrier). JAS and Raben have a Test host, so they keep the switch.
const NO_SANDBOX = new Set(["palletways", "dbschenker"]);
const HAS_SURCHARGE_CONFIG = new Set(["inpost", "apc"]);
// APC's only contract surcharge in the model is the monthly fuel
// percentage (notice of 2026-09-10: 4.25% from 14 Sep, reviewed monthly).
const APC_SURCHARGE_FIELDS: Array<{ key: keyof CarrierSurchargeConfig; labelKey: string; step: number; unit: string }> = [
    { key: "fuel_pct", labelKey: "carriers_surcharge_fuel", step: 0.05, unit: "%" },
];

const SURCHARGE_FIELDS: Array<{ key: keyof CarrierSurchargeConfig; labelKey: string; step: number; unit: string }> = [
    { key: "fuel_pct", labelKey: "carriers_surcharge_fuel", step: 0.5, unit: "%" },
    { key: "decarbonisation", labelKey: "carriers_surcharge_decarb", step: 0.01, unit: "£" },
    { key: "northern_ireland", labelKey: "carriers_surcharge_ni", step: 0.01, unit: "£" },
    { key: "remote_area", labelKey: "carriers_surcharge_remote", step: 0.01, unit: "£" },
    { key: "oog1", labelKey: "carriers_surcharge_oog1", step: 0.01, unit: "£" },
    { key: "oog2", labelKey: "carriers_surcharge_oog2", step: 0.01, unit: "£" },
];

const CarrierCard = ({ carrier, hint, onUpdated }: Props) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showSavings, setShowSavings] = useState(false);
    const [showClaims, setShowClaims] = useState(false);
    const code = carrier.carrierCode;
    // Display the EFFECTIVE surcharges (defaults filled in); edits patch the
    // stored config. Older BEs don't send effectiveSurcharges.
    const surcharges: CarrierSurchargeConfig = carrier.effectiveSurcharges ?? carrier.config?.surcharges ?? {};
    const remoteList: string[] = surcharges.remote_area_postcodes ?? [];
    // InPost register ONE endpoint: production. Staging serves the same
    // route, but the URL to hand over is the production one.
    const webhookUrl = `${PRODUCTION_API_ORIGIN}/api/v1/inpost/webhook`;
    const thisEnvWebhookUrl = `${String(API_BASE).replace(/\/$/, "")}/api/v1/inpost/webhook`;

    // Mint the webhook signing secret server-side; the plaintext comes back
    // exactly once for the hand-over to InPost, the carrier comes back masked.
    const generateWebhookSecret = async (): Promise<string> => {
        if (readOnly) throw new Error(t("read_only_mode"));
        setSaving(true);
        setError(null);
        try {
            const res = await apiFetch(`/admin/carriers/${carrier.id}/webhook-secret`, { method: "POST" });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
            onUpdated(json.carrier as CarrierConfig);
            return String(json.webhookSecret);
        } finally {
            setSaving(false);
        }
    };

    const patchSurcharges = (next: Partial<CarrierSurchargeConfig>) =>
        patchCarrier({ config: { ...carrier.config, surcharges: { ...surcharges, ...next } } });

    const patchCarrier = async (body: CarrierPatch) => {
        if (readOnly) return;
        setSaving(true);
        setError(null);
        try {
            const res = await apiFetch(`/admin/carriers/${carrier.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
            onUpdated(json.carrier as CarrierConfig);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    };

    const pillClass = carrier.enabled
        ? "admin-integrations-status-pill admin-integrations-status-pill--ok"
        : "admin-integrations-status-pill admin-integrations-status-pill--off";
    const pillLabel = carrier.enabled
        ? t("integrations_status_pill_enabled")
        : t("integrations_status_pill_disabled");

    return (
        <details className="admin-integrations-card-details" id={carrier.carrierCode}>
            <summary className="admin-integrations-card-summary">
                <span className="admin-integrations-card-summary-title">
                    {carrier.displayName}
                </span>
                {carrier.enabled && carrier.environment === "training" && (
                    <span className="admin-integrations-pill admin-integrations-pill--dry-run">
                        {t("carriers_env_training")}
                    </span>
                )}
                <span className={pillClass}>{pillLabel}</span>
            </summary>

            <div className="admin-integrations-card-body carrier-card-body">
                {hint && <p className="admin-integrations-card-hint">{hint}</p>}

                {/* Top line: master enable + environment, side by side. */}
                <div className="carrier-topline">
                    <div className="admin-integrations-form-row--toggle">
                        <label className="admin-settings-toggle">
                            <input
                                type="checkbox"
                                checked={carrier.enabled}
                                disabled={saving || readOnly}
                                onChange={(e) => patchCarrier({ enabled: e.target.checked })}
                            />
                            <span className="admin-settings-slider" />
                        </label>
                        <span className="admin-integrations-toggle-label">{t("carriers_enabled_toggle")}</span>
                    </div>
                    {!NO_SANDBOX.has(code) && (
                    <div className="carrier-topline-env">
                        <span className="admin-integrations-field-label">{t("carriers_environment")}</span>
                        <div className="admin-integrations-mode-toggle">
                            <button
                                type="button"
                                className={`admin-integrations-mode-btn${carrier.environment === "training" ? " admin-integrations-mode-btn--active" : ""}`}
                                disabled={saving || readOnly || carrier.environment === "training"}
                                onClick={() => patchCarrier({ environment: "training" })}
                            >
                                {t("carriers_env_training")}
                            </button>
                            <button
                                type="button"
                                className={`admin-integrations-mode-btn${carrier.environment === "live" ? " admin-integrations-mode-btn--active admin-integrations-mode-btn--live" : ""}`}
                                disabled={saving || readOnly || carrier.environment === "live"}
                                onClick={() => patchCarrier({ environment: "live" })}
                            >
                                {t("carriers_env_live")}
                            </button>
                        </div>
                    </div>
                    )}
                </div>
                <p className="admin-integrations-field-hint">{t(ENVIRONMENT_HINT_KEY[code] ?? "carriers_environment_hint")}</p>

                {/* Consignment merging (APC): add-box + cross-channel. */}
                {SUPPORTS_ADD_BOX.has(code) && (
                    <CarrierSection
                        title={t("carriers_section_merging")}
                        badge={{ text: carrier.addBoxToExistingConsignment ? t("carriers_badge_on") : t("carriers_badge_off"), tone: carrier.addBoxToExistingConsignment ? "ok" : "off" }}
                        summary={carrier.addBoxToExistingConsignment
                            ? (carrier.crossChannelAddBoxToExistingConsignment ? t("carriers_merging_digest_cross") : t("carriers_merging_digest_same"))
                            : null}
                    >
                        <div className="admin-integrations-form-row--toggle">
                            <label className="admin-settings-toggle">
                                <input
                                    type="checkbox"
                                    checked={carrier.addBoxToExistingConsignment}
                                    disabled={saving || readOnly}
                                    onChange={(e) => patchCarrier({ addBoxToExistingConsignment: e.target.checked })}
                                />
                                <span className="admin-settings-slider" />
                            </label>
                            <span className="admin-integrations-toggle-label">{t("carriers_add_box_toggle")}</span>
                        </div>
                        <p className="admin-integrations-field-hint">{t("carriers_add_box_hint")}</p>
                        <div className="admin-integrations-form-row--toggle admin-integrations-form-row--nested">
                            <label className="admin-settings-toggle">
                                <input
                                    type="checkbox"
                                    checked={carrier.crossChannelAddBoxToExistingConsignment}
                                    disabled={saving || readOnly}
                                    onChange={(e) => patchCarrier({ crossChannelAddBoxToExistingConsignment: e.target.checked })}
                                />
                                <span className="admin-settings-slider" />
                            </label>
                            <span className="admin-integrations-toggle-label">{t("carriers_cross_channel_add_box_toggle")}</span>
                        </div>
                        <p className="admin-integrations-field-hint">{t("carriers_cross_channel_add_box_hint")}</p>
                        <button type="button" className="btn btn-ghost carrier-savings-btn" onClick={() => setShowSavings(true)}>
                            <PiggyBank size={16} style={{ marginRight: 6, verticalAlign: "-3px" }} />
                            {t("savings_open_button")}
                        </button>
                        {showSavings && <CarrierSavingsModal onClose={() => setShowSavings(false)} />}
                    </CarrierSection>
                )}

                {/* Carrier claims (APC): scheduled claims sheet by email. */}
                {SUPPORTS_CLAIMS.has(code) ? (
                    <CarrierSection
                        title={t("carriers_section_claims")}
                        badge={{ text: carrier.claimsEnabled ? t("carriers_badge_on") : t("carriers_badge_off"), tone: carrier.claimsEnabled ? "ok" : "off" }}
                        summary={carrier.claimsEnabled ? t("carriers_claims_digest", { to: carrier.claimsConfig?.toAddress ?? "" }) : null}
                    >
                        <div className="admin-integrations-form-row--toggle">
                            <label className="admin-settings-toggle">
                                <input
                                    type="checkbox"
                                    checked={carrier.claimsEnabled}
                                    disabled={saving || readOnly}
                                    onChange={(e) => patchCarrier({ claimsEnabled: e.target.checked })}
                                />
                                <span className="admin-settings-slider" />
                            </label>
                            <span className="admin-integrations-toggle-label">{t("carriers_claims_toggle")}</span>
                            <button
                                type="button"
                                className="carrier-claims-gear"
                                aria-label={t("carriers_claims_settings")}
                                title={t("carriers_claims_settings")}
                                onClick={() => setShowClaims(true)}
                            >
                                <Settings2 size={16} />
                            </button>
                        </div>
                        <p className="admin-integrations-field-hint">{t("carriers_claims_hint")}</p>
                        {showClaims && (
                            <CarrierClaimsSettingsModal carrier={carrier} onClose={() => setShowClaims(false)} onUpdated={onUpdated} />
                        )}
                    </CarrierSection>
                ) : (
                    <p className="admin-integrations-field-hint carrier-card-note">{t("carriers_claims_unsupported")}</p>
                )}

                {/* Tracking webhooks (InPost). Opens by itself until the
                    signing secret is in place. */}
                {HAS_WEBHOOKS.has(code) && (
                    <CarrierSection
                        title={t("carriers_webhook_heading")}
                        badge={carrier.webhookSecret.set
                            ? { text: t("carriers_badge_secret_set"), tone: "ok" }
                            : { text: t("carriers_badge_needs_secret"), tone: "warn" }}
                        summary={carrier.webhookLastReceivedAt
                            ? t("carriers_webhook_last_received", { at: new Date(carrier.webhookLastReceivedAt).toLocaleString() })
                            : t("carriers_webhook_none_yet")}
                        defaultOpen={!carrier.webhookSecret.set}
                    >
                        <p className="admin-integrations-field-hint">{t("carriers_webhook_hint")}</p>
                        <input
                            className="admin-integrations-input admin-integrations-input--mono"
                            readOnly
                            value={webhookUrl}
                            onFocus={(e) => e.target.select()}
                        />
                        {thisEnvWebhookUrl !== webhookUrl && (
                            <p className="admin-integrations-field-hint">{t("carriers_webhook_this_env", { url: thisEnvWebhookUrl })}</p>
                        )}
                        <SecretField
                            label={t("carriers_webhook_secret")}
                            hint={t("carriers_webhook_secret_hint")}
                            secret={carrier.webhookSecret}
                            minLength={8}
                            onSave={(plaintext) => patchCarrier({ webhookSecret: plaintext })}
                            onClear={() => patchCarrier({ webhookSecret: null })}
                            onGenerate={generateWebhookSecret}
                        />
                    </CarrierSection>
                )}

                {/* Contract surcharges (InPost): the cost model's inputs. */}
                {code === "apc" && (
                    <CarrierSection
                        title={t("carriers_surcharges_heading")}
                        summary={surcharges.fuel_pct
                            ? t("carriers_apc_fuel_digest", { pct: String(surcharges.fuel_pct), from: surcharges.fuel_from ?? "—" })
                            : t("carriers_apc_fuel_none")}
                    >
                        <p className="admin-integrations-field-hint">{t("carriers_apc_surcharges_hint")}</p>
                        <div className="carrier-field-grid carrier-field-grid--4">
                            {APC_SURCHARGE_FIELDS.map(({ key, labelKey, step, unit }) => (
                                <div className="admin-integrations-field" key={key}>
                                    <label className="admin-integrations-field-label">{t(labelKey)} ({unit})</label>
                                    <input
                                        type="number" min={0} step={step}
                                        className="admin-integrations-input"
                                        defaultValue={surcharges[key] as number | undefined ?? ""}
                                        disabled={saving || readOnly}
                                        onBlur={(e) => {
                                            const raw = e.target.value.trim();
                                            const n = raw === "" ? 0 : Number(raw);
                                            if (!Number.isFinite(n) || n < 0) return;
                                            if (n !== (surcharges[key] ?? 0)) patchSurcharges({ [key]: n });
                                        }}
                                    />
                                </div>
                            ))}
                            <div className="admin-integrations-field">
                                <label className="admin-integrations-field-label">{t("carriers_surcharge_fuel_from")}</label>
                                <input
                                    type="date"
                                    className="admin-integrations-input"
                                    defaultValue={surcharges.fuel_from ?? ""}
                                    disabled={saving || readOnly}
                                    onBlur={(e) => {
                                        const v = e.target.value.trim();
                                        if (v !== (surcharges.fuel_from ?? "")) patchSurcharges({ fuel_from: v || null });
                                    }}
                                />
                            </div>
                        </div>
                    </CarrierSection>
                )}

                {HAS_SURCHARGE_CONFIG.has(code) && code !== "apc" && (
                    <CarrierSection
                        title={t("carriers_surcharges_heading")}
                        summary={[
                            `${t("carriers_surcharge_fuel_short")} ${surcharges.fuel_pct ?? 0}%`,
                            `${t("carriers_surcharge_decarb_short")} £${Number(surcharges.decarbonisation ?? 0).toFixed(2)}`,
                            `${t("carriers_surcharge_ni_short")} £${Number(surcharges.northern_ireland ?? 0).toFixed(2)}`,
                            `${t("carriers_surcharge_remote_short")} £${Number(surcharges.remote_area ?? 0).toFixed(2)} (${remoteList.length})`,
                            ...(surcharges.oog_on_band !== false ? [`M +£${Number(surcharges.oog1 ?? 0).toFixed(2)} · L +£${Number(surcharges.oog2 ?? 0).toFixed(2)}`] : []),
                        ].join(" · ")}
                    >
                        <p className="admin-integrations-field-hint">{t("carriers_surcharges_hint")}</p>
                        <div className="carrier-field-grid carrier-field-grid--4">
                            {SURCHARGE_FIELDS.map(({ key, labelKey, step, unit }) => (
                                <div className="admin-integrations-field" key={key}>
                                    <label className="admin-integrations-field-label">{t(labelKey)} ({unit})</label>
                                    <input
                                        type="number" min={0} step={step}
                                        className="admin-integrations-input"
                                        defaultValue={surcharges[key] as number | undefined ?? ""}
                                        disabled={saving || readOnly}
                                        onBlur={(e) => {
                                            const raw = e.target.value.trim();
                                            const n = raw === "" ? 0 : Number(raw);
                                            if (!Number.isFinite(n) || n < 0) return;
                                            if (n !== (surcharges[key] ?? 0)) patchSurcharges({ [key]: n });
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                        <label className="admin-integrations-form-row--toggle">
                            <input
                                type="checkbox"
                                checked={surcharges.oog_on_band !== false}
                                disabled={saving || readOnly}
                                onChange={(e) => patchSurcharges({ oog_on_band: e.target.checked })}
                            />
                            <span className="admin-integrations-toggle-label">{t("carriers_surcharge_oog_on_band")}</span>
                        </label>
                        <div className="admin-integrations-field">
                            <label className="admin-integrations-field-label">{t("carriers_surcharge_remote_postcodes")}</label>
                            <p className="admin-integrations-field-hint">
                                {t("carriers_surcharge_remote_postcodes_hint")}{" "}
                                {carrier.remoteAreaCustom
                                    ? t("carriers_surcharge_remote_custom", { n: String(remoteList.length) })
                                    : t("carriers_surcharge_remote_published", { n: String(remoteList.length) })}
                            </p>
                            <textarea
                                key={`${carrier.remoteAreaCustom ? "custom" : "published"}-${remoteList.length}`}
                                className="admin-integrations-input admin-integrations-input--mono"
                                rows={3}
                                defaultValue={remoteList.join(", ")}
                                disabled={saving || readOnly}
                                onBlur={(e) => {
                                    // Comma/newline separated — a space is part of a
                                    // district pattern ("PA48 *"), never a separator.
                                    const list = e.target.value.split(/,|\n/).map((s) => s.trim().toUpperCase()).filter(Boolean);
                                    if (list.join(",") !== remoteList.join(",")) patchSurcharges({ remote_area_postcodes: list });
                                }}
                            />
                            {carrier.remoteAreaCustom && (
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    disabled={saving || readOnly}
                                    onClick={() => patchSurcharges({ remote_area_postcodes: null })}
                                >
                                    {t("carriers_surcharge_remote_restore", { n: String(carrier.remoteAreaDefaultCount ?? 0) })}
                                </button>
                            )}
                        </div>
                    </CarrierSection>
                )}

                {error && <p className="admin-integrations-field-error">{error}</p>}

                {/* Accounts: one per warehouse (and per credential set on
                    multi-account carriers, e.g. InPost 2Door / OOH). */}
                <div className="carrier-accounts-heading">
                    <span className="admin-integrations-field-label">{t("carriers_accounts_heading")}</span>
                    <span className="carrier-section-digest">
                        {t("carriers_accounts_digest", {
                            enabled: String(carrier.accounts.filter((a) => a.enabled).length),
                            total: String(carrier.accounts.length),
                        })}
                    </span>
                </div>
                {carrier.accounts.map((account) => (
                    <CarrierAccountPanel
                        key={account.id}
                        account={account}
                        carrierCode={code}
                        environment={carrier.environment}
                        onUpdated={onUpdated}
                    />
                ))}
            </div>
        </details>
    );
};

export default CarrierCard;
